import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { apiService } from "../services/apiService";
import { useApiAuth } from "../hooks/useApiAuth";

interface NotificationsContextType {
  notificationCount: number;
  refetch: () => Promise<void>;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notificationCount, setNotificationCount] = useState(0);
  const { isApiReady } = useApiAuth();

  const fetchNotifications = useCallback(async () => {
    if (!isApiReady) {
      return;
    }

    try {
      const response = await apiService.getNotifications();
      if (response.success && response.data) {
        setNotificationCount(response.data.notifications);
      }
    } catch (error) {
      console.error("Failed to load notifications:", error);
    }
  }, [isApiReady]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const value = {
    notificationCount,
    refetch: fetchNotifications,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider",
    );
  }
  return context;
};
